import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

import { bolt11ToSats, classifyZapReceipt } from '@/lib/zaps';

export interface ZapTotalData {
  /** Verified receipts (p tag == event author). Ranking input. */
  total: number;
  /** Legacy receipts with no p tag — display-only. */
  unverifiedTotal: number;
  unverifiedCount: number;
}

/** Sums zap receipts (kind 9735) targeting a specific event, in sats. */
export function useZapTotal(event: NostrEvent | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['stashstr', 'zapTotal', event?.id ?? '', event?.pubkey ?? ''],
    queryFn: async (c): Promise<ZapTotalData> => {
      if (!event) throw new Error('No event');
      const results = await nostr.query(
        [
          { kinds: [9735], '#e': [event.id], limit: 100 },
          { kinds: [9735], '#E': [event.id], limit: 100 },
        ],
        { signal: c.signal },
      );

      let total = 0;
      let unverifiedTotal = 0;
      let unverifiedCount = 0;
      for (const receipt of results) {
        const verdict = classifyZapReceipt(receipt.tags, event.pubkey);
        if (verdict === 'spoof') continue;
        const bolt11 = receipt.tags.find(([t]) => t === 'bolt11')?.[1];
        const sats = bolt11 ? bolt11ToSats(bolt11) : 0;
        if (verdict === 'verified') {
          total += sats;
        } else {
          unverifiedTotal += sats;
          unverifiedCount++;
        }
      }
      return { total, unverifiedTotal, unverifiedCount };
    },
    staleTime: 30_000,
    enabled: Boolean(event),
  });
}
